import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import GMBConnectionStatus from './GMBConnectionStatus';
import GmbNotificationBell from './GmbNotificationBell';
import CreditsWidget from '../../../components/CreditsWidget';
import ProfileAvatar from '../../../components/ProfileAvatar';
import LogoutButton from '../../../components/LogoutButton';

interface GMBHeaderProps {
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
  pageTitle: string;
  pageSubtitle: string;
  isConnected: boolean;
  connectionData: {
    business_name: string | null;
    last_synced_label: string | null;
  } | null;
  onConnect: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  isSyncing: boolean;
  onNavigate?: (section: string) => void;
}

export default function GMBHeader({
  darkMode,
  setDarkMode,
  pageTitle,
  pageSubtitle,
  isConnected,
  connectionData,
  onConnect,
  onSync,
  onDisconnect,
  isSyncing,
  onNavigate,
}: GMBHeaderProps) {
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem('brand_name') || '');
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    const storedLogo = localStorage.getItem('logo_url');
    return storedLogo ? storedLogo.replace(/"/g, '') || null : null;
  });

  useEffect(() => {
    async function fetchBrandProfile() {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      try {
        const res = await apiService.getWebsiteProfile(userId);
        const bData = res?.data?.business_profile || res?.business_profile || res?.data || res;
        if (bData?.business_name) {
          setBrandName(bData.business_name);
          localStorage.setItem('brand_name', bData.business_name);
        }
        if (bData?.logo_url) {
          const cleanedLogo = bData.logo_url.replace(/"/g, '');
          localStorage.setItem('logo_url', cleanedLogo);
          setLogoUrl(cleanedLogo);
        }
      } catch (err) {
        console.error('Failed to fetch brand profile in GMBHeader', err);
      }
    }
    fetchBrandProfile();

    const handleLogoUpdated = () => {
      setLogoUrl((localStorage.getItem('logo_url') || '').replace(/"/g, ''));
      setBrandName(localStorage.getItem('brand_name') || '');
    };
    window.addEventListener('logo_updated', handleLogoUpdated);
    return () => window.removeEventListener('logo_updated', handleLogoUpdated);
  }, []);

  return (
    <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {pageTitle}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{pageSubtitle}</p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Home Link */}
          <Link
            to="/marketing-dashboard"
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-semibold border ${
              darkMode
                ? 'text-gray-400 border-gray-700 hover:text-white hover:bg-gray-700'
                : 'text-gray-500 border-gray-100 hover:text-[#5C4FCF] hover:bg-[#5C4FCF]/5'
            }`}
          >
            <i className="ri-home-4-line text-lg"></i>
            <span className="hidden lg:inline">Home</span>
          </Link>

          {isSyncing && (
            <span
              role="status"
              aria-live="polite"
              className={`flex items-center gap-2 text-sm font-medium ${
                darkMode ? 'text-teal-400' : 'text-teal-600'
              }`}
            >
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              Syncing GMB...
            </span>
          )}
          {isConnected ? (
            <GMBConnectionStatus
              connectionData={connectionData}
              onSync={onSync}
              onDisconnect={onDisconnect}
              isSyncing={isSyncing}
              darkMode={darkMode}
            />
          ) : (
            <button
              onClick={onConnect}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'text-teal-400 hover:bg-gray-700'
                  : 'text-teal-600 hover:bg-teal-50'
              }`}
            >
              <i className="ri-links-line"></i>
              Connect GMB
            </button>
          )}

          <GmbNotificationBell onNavigate={onNavigate} />

          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 rounded-lg transition-colors ${
              darkMode
                ? 'text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className={`text-xl ${darkMode ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
          </button>

          {/* Credits Widget */}
          <CreditsWidget />

          {/* Brand Profile Button */}
          <button
            onClick={() => navigate('/profile')}
            className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full transition-colors cursor-pointer group ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            title="Profile"
          >
            <ProfileAvatar logoUrl={logoUrl} fallbackName={brandName} size="sm" />
            <span
              className={`text-sm font-medium whitespace-nowrap ${
                darkMode ? 'text-gray-200 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
              }`}
            >
              Profile
            </span>
            <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
          </button>

          {/* Logout Button */}
          <LogoutButton
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-semibold border ${
              darkMode
                ? 'text-gray-400 border-gray-700 hover:text-white hover:bg-gray-700'
                : 'text-gray-500 border-gray-100 hover:text-[#5C4FCF] hover:bg-[#5C4FCF]/5'
            }`}
          >
            <i className="ri-logout-box-r-line text-lg"></i>
            <span className="hidden lg:inline">Logout</span>
          </LogoutButton>
        </div>
      </div>
    </header>
  );
}
